import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatCents, formatDate } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card, Pill, Loader, Button, PageHeader, Modal, Input, EmptyState } from "@/components/ui/Primitives";
import { Building2, MapPin, ArrowLeft, Plus, BedDouble, Bath, ExternalLink, Home, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function PropertyDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [prop, setProp] = useState(null);
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ label: "", bedrooms: 1, bathrooms: 1, rent_amount: "", currency: "cad" });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [p, u] = await Promise.all([
        api.get(`/properties/${id}`),
        api.get(`/units?property_id=${id}`),
      ]);
      // Backend GET /api/properties/{id} returns {property:{...}, units:[...], pnl:{...}}
      // — unwrap it. Same for units endpoint when wrapped.
      setProp(p.data?.property || p.data);
      setUnits(Array.isArray(u.data) ? u.data : (u.data?.units || []));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [id]);

  const submitUnit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/units", {
        property_id: id,
        label: form.label.trim(),
        bedrooms: Number(form.bedrooms) || 1,
        bathrooms: Number(form.bathrooms) || 1,
        rent_amount: Math.round(Number(form.rent_amount) * 100),
        currency: form.currency,
      });
      toast.success("Unit added");
      setOpen(false);
      setForm({ label: "", bedrooms: 1, bathrooms: 1, rent_amount: "", currency: "cad" });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create unit");
    } finally { setSaving(false); }
  };

  if (loading) return <Loader/>;
  if (!prop) return <EmptyState icon={Building2} title="Property not found"/>;

  const isLandlord = user?.role === "landlord";

  return (
    <div>
      <Link to="/properties" className="inline-flex items-center gap-2 text-sm font-semibold text-slate-600 hover:text-slate-900 mb-4" data-testid="back-to-properties">
        <ArrowLeft className="w-4 h-4"/> All properties
      </Link>

      <div className="rounded-2xl overflow-hidden bg-white border border-slate-200">
        <div className="aspect-[16/6] bg-slate-100 relative group">
          {prop.image_url ? (
            <img src={prop.image_url} alt={prop.name} className="w-full h-full object-cover"/>
          ) : (
            <div className="w-full h-full flex items-center justify-center text-slate-300"><Building2 className="w-16 h-16"/></div>
          )}
          {isLandlord && prop.image_url && (
            <button
              data-testid="delete-property-photo"
              onClick={async () => {
                if (!confirm("Remove this property photo?")) return;
                try {
                  await api.patch(`/properties/${prop.property_id}`, { image_url: null });
                  toast.success("Photo removed");
                  load();
                } catch (err) {
                  toast.error(err?.response?.data?.detail || "Could not remove photo");
                }
              }}
              className="absolute top-3 right-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-red-600 hover:bg-red-700 text-white text-xs font-semibold shadow-lg z-10"
            >
              <Trash2 className="w-3.5 h-3.5"/> Remove photo
            </button>
          )}
        </div>
        <div className="p-6 md:p-8 flex flex-col md:flex-row md:items-end justify-between gap-4">
          <div>
            <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight text-slate-950">{prop.name}</h1>
            <div className="mt-2 flex items-center gap-1.5 text-slate-600">
              <MapPin className="w-4 h-4"/> {prop.address}
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Pill tone="slate"><Home className="w-3 h-3"/> {prop.units_total} {prop.units_total === 1 ? "unit" : "units"}</Pill>
            <a href={`/p/${prop.property_id}`} target="_blank" rel="noreferrer" data-testid="property-public-link">
              <Button variant="secondary"><ExternalLink className="w-4 h-4"/> Public page</Button>
            </a>
          </div>
        </div>
      </div>

      <div className="mt-8 flex items-center justify-between">
        <div>
          <h2 className="font-display text-xl font-bold text-slate-900">Units</h2>
          <p className="text-sm text-slate-500">{units.length} unit{units.length === 1 ? "" : "s"}</p>
        </div>
        {isLandlord && (
          <Button onClick={() => setOpen(true)} data-testid="add-unit-button"><Plus className="w-4 h-4"/> Add unit</Button>
        )}
      </div>

      {units.length === 0 ? (
        <div className="mt-5">
          <EmptyState icon={Home} title="No units yet" description={isLandlord ? "Add units to this property to start creating leases." : "No units have been added yet."}/>
        </div>
      ) : (
        <div className="mt-5 grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {units.map((u) => (
            <Card key={u.unit_id} className="p-5 card-lift" data-testid={`unit-card-${u.unit_id}`}>
              <div className="flex items-start justify-between">
                <div>
                  <div className="font-display text-lg font-bold text-slate-900">{u.label}</div>
                  <div className="mt-1 text-xs text-slate-500 uppercase tracking-wider">{u.currency?.toUpperCase()} · Monthly rent</div>
                </div>
                <Pill tone={u.tenant_id ? "green" : "amber"}>{u.tenant_id ? "Occupied" : "Vacant"}</Pill>
              </div>
              <div className="mt-3 font-display text-2xl font-bold text-slate-950">{formatCents(u.rent_amount, u.currency?.toUpperCase())}</div>
              <div className="mt-3 flex items-center gap-4 text-sm text-slate-600">
                <span className="inline-flex items-center gap-1"><BedDouble className="w-4 h-4"/> {u.bedrooms} BR</span>
                <span className="inline-flex items-center gap-1"><Bath className="w-4 h-4"/> {u.bathrooms} BA</span>
              </div>
            </Card>
          ))}
        </div>
      )}

      <Modal open={open} onClose={() => setOpen(false)} title="Add unit" testid="add-unit-modal">
        <form onSubmit={submitUnit} className="space-y-4">
          <Input label="Unit label" required placeholder="Apt 1A" value={form.label} onChange={(e)=>setForm({...form, label:e.target.value})} testid="unit-label-input"/>
          <div className="grid grid-cols-2 gap-3">
            <Input label="Bedrooms" type="number" min="0" value={form.bedrooms} onChange={(e)=>setForm({...form, bedrooms:e.target.value})} testid="unit-bedrooms-input"/>
            <Input label="Bathrooms" type="number" min="0" value={form.bathrooms} onChange={(e)=>setForm({...form, bathrooms:e.target.value})} testid="unit-bathrooms-input"/>
          </div>
          <Input label="Monthly rent (CAD)" type="number" min="0" step="0.01" required placeholder="2200" value={form.rent_amount} onChange={(e)=>setForm({...form, rent_amount:e.target.value})} testid="unit-rent-input"/>
          <div className="flex items-center justify-end gap-2 pt-2">
            <Button type="button" variant="ghost" onClick={() => setOpen(false)} data-testid="unit-cancel">Cancel</Button>
            <Button type="submit" disabled={saving} data-testid="unit-create-submit">{saving ? "Creating…" : "Create unit"}</Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
